import {hpcAttrs} from '@github-ui/hpc'
import {ListView} from '@github-ui/list-view'
import {testIdProps} from '@github-ui/test-id-props'
import {ProjectTemplateIcon, TableIcon} from '@primer/octicons-react'
import {Button, Spinner, VisuallyHidden} from '@primer/react'
import {Blankslate} from '@primer/react/experimental'
import {type ComponentProps, Fragment, type ReactNode} from 'react'

import type {ProjectListLoadingState} from './project-list-loading-state'
import {type ProjectListItem, ProjectListRow} from './ProjectListRow'
import styles from './RepositoryProjects.module.css'

export interface ProjectListEmptyState {
  heading: string
  description?: ReactNode
  action?: ReactNode
  announcement?: string
}

interface ProjectListViewProps<TProject extends ProjectListItem> {
  projects: readonly TProject[]
  title: string
  metadata?: ComponentProps<typeof ListView>['metadata']
  emptyState: ProjectListEmptyState
  query: string
  hasNextPage: boolean
  onLoadMore: () => void
  loadingState?: ProjectListLoadingState
  isTemplateView?: boolean
  renderRow?: (project: TProject) => ReactNode
  listTestId?: string
}

export function ProjectListView<TProject extends ProjectListItem>({
  projects,
  title,
  metadata,
  emptyState,
  query,
  hasNextPage,
  onLoadMore,
  loadingState = 'ready',
  isTemplateView = false,
  renderRow,
  listTestId = 'project-list-view',
}: ProjectListViewProps<TProject>) {
  // Keep placeholder results silent while a different query is loading.
  const showResultsAnnouncement = loadingState === 'ready' || loadingState === 'loadingMore'
  const liveRegionMessage = showResultsAnnouncement
    ? buildResultsAnnouncement(projects.length, query, isTemplateView, emptyState)
    : ''
  const Icon = isTemplateView ? ProjectTemplateIcon : TableIcon
  const label = isTemplateView ? 'project templates' : 'projects'
  const hasActiveQuery = query.trim().length > 0

  return (
    <>
      <VisuallyHidden role="status" aria-live="polite" aria-atomic="true">
        {liveRegionMessage}
      </VisuallyHidden>
      <div className={styles.projectListWrapper} {...hpcAttrs()} {...testIdProps('project-list-wrapper')}>
        <ListView
          {...testIdProps(listTestId)}
          title={title}
          metadata={metadata}
          titleHeaderTag="h2"
          singularUnits="project"
          pluralUnits="projects"
          as={projects.length > 0 ? 'ul' : 'div'}
          role={projects.length > 0 ? 'list' : 'status'}
        >
          {projects.length > 0
            ? projects.map(project => (
                <Fragment key={project.id}>
                  {renderRow ? renderRow(project) : <ProjectListRow project={project} />}
                </Fragment>
              ))
            : null}
        </ListView>
        {loadingState === 'loading' ? (
          <div className={styles.loadingContainer} role="status" aria-label="Loading projects">
            <Spinner size="large" />
          </div>
        ) : (
          projects.length === 0 && (
            <div className={styles.emptyState}>
              <Blankslate>
                <Blankslate.Visual>
                  <Icon size="medium" />
                </Blankslate.Visual>
                <Blankslate.Heading>
                  {hasActiveQuery ? `No ${label} matched your search` : emptyState.heading}
                </Blankslate.Heading>
                {hasActiveQuery ? (
                  <Blankslate.Description>
                    Try a different search term or clear the search to see all {label}.
                  </Blankslate.Description>
                ) : (
                  <>
                    {emptyState.description != null && (
                      <Blankslate.Description>{emptyState.description}</Blankslate.Description>
                    )}
                    {emptyState.action}
                  </>
                )}
              </Blankslate>
            </div>
          )
        )}
      </div>
      {hasNextPage && (
        <div className={styles.loadMoreButtonWrapper}>
          <Button
            variant="invisible"
            onClick={onLoadMore}
            className={styles.loadMoreButton}
            data-testid="load-more-projects-button"
            loading={loadingState === 'loadingMore'}
          >
            Load more projects
          </Button>
        </div>
      )}
    </>
  )
}

function buildResultsAnnouncement(
  count: number,
  query: string,
  isTemplateView: boolean,
  emptyState: ProjectListEmptyState,
): string {
  const noun = isTemplateView ? 'template' : 'project'
  const plural = isTemplateView ? 'templates' : 'projects'
  const trimmedQuery = query.trim()
  if (count === 0) {
    if (trimmedQuery.length > 0) {
      return `No ${plural} matched \u201C${trimmedQuery}\u201D`
    }
    return emptyState.announcement ?? emptyState.heading
  }
  if (count === 1) {
    return `1 ${noun}`
  }
  return `${count} ${plural}`
}
